<script setup lang="ts">
    import { computed, onMounted, ref } from 'vue';
    import { useRouter } from 'vue-router';
    import _ from 'lodash';
    import { getReleaseLink } from '@/shared/helpers/routing';
    import { createScopeCodeSetRelease, getScopes, updateCodeSet } from '@/shared/helpers/api.ts';
    import { compareByProperty } from '@/shared/helpers/utils';
    import { requireCanEditLibrary as vRequireCanEditLibrary } from '@/shared/directives/requirePermission';
    import { validateRegex } from '@/shared/helpers/validateRegex.ts';
    import { http } from '@/shared/httpWrapper';
    import useShow from '@/shared/composables/useShow';
    import type { CodeSetProp, Library, ScopeCodeSetChange, ScopeCodeSetRelease } from '@/shared/helpers/api/types/entities';
    import MarkdownEditorComponent from '@/shared/components/MarkdownEditorComponent.vue';

    const props = defineProps<{
        codeset: CodeSetProp;
        library: Library;
        scopes: string[];
    }>();

    const emit = defineEmits<{
        refresh: [];
        close: [];
    }>();

    const router = useRouter();
    const { showError } = useShow();

    const allScopes = ref<string[]>([]);
    const scopeCodeSetChanges = ref<ScopeCodeSetChange[]>([]);
    const selectedScope = ref<string | null>(null);
    const newDescription = ref<string | null>(null);
    const newDefinition = ref<string | null>(null);
    const newRegex = ref<string | null>(null);
    const newRegexErrorMessage = ref<string | null>(null);
    const newLockedForDelete = ref<boolean | null>(null);
    const newIsReleaseReady = ref<boolean | null>(null);
    const loading = ref(true);
    const saveLoading = ref(false);

    const scopeViewModel = computed(() => {
        const scopesNotChanged = props.scopes
            .map((scope) => ({ name: scope, changeType: undefined as string | undefined }))
            .filter(
                (scope) =>
                    !scopeCodeSetChanges.value
                        .map((c) => c.scopeName)
                        .includes(scope.name)
            );
        return (scopeCodeSetChanges.value
            .map((scope) => ({
                name: scope.scopeName,
                changeType: scope.changeType as string | undefined
            }))
            .concat(scopesNotChanged) as { name: string; changeType: string | undefined }[])
            .sort(compareByProperty('name'));
    });

    const scopesNotSelected = computed(() => {
        const selectedScopes = scopeViewModel.value.map(x => x.name);
        return allScopes.value.filter(scopeName => !selectedScopes.includes(scopeName));
    });

    const canEditRegex = computed(() => {
        return (
            props.library.isGlobal
            || !props.library.isGlobal && props.scopes.length > 0
        );
    });

    const regexError = computed(() => {
        return validateRegex(newRegex.value ?? '');
    });

    const hasChanges = computed(() => (
        newDescription.value !== props.codeset.description
        || newDefinition.value !== (props.codeset.definition ?? '')
        || newLockedForDelete.value !== props.codeset.lockedForDelete
        || newIsReleaseReady.value !== props.codeset.isReleaseReady
        || newRegex.value !== props.codeset.codeNameRegex
        || newRegexErrorMessage.value !== props.codeset.codeNameRegexErrorMessage
        || scopeCodeSetChanges.value.length > 0
    ));

    const saveDisabled = computed(() => loading.value || !!regexError.value || !hasChanges.value);

    onMounted(async () => {
        if (!props.library.isGlobal) {
            allScopes.value = await getScopes(http, props.library.scopeType).then(
                (vals) => vals.map(v => v.identity).sort()
            );
        }
        newDescription.value = props.codeset.description;
        newDefinition.value = props.codeset.definition ?? '';
        newRegex.value = props.codeset.codeNameRegex;
        newRegexErrorMessage.value = props.codeset.codeNameRegexErrorMessage;
        newLockedForDelete.value = props.codeset.lockedForDelete;
        newIsReleaseReady.value = props.codeset.isReleaseReady;
        loading.value = false;
    });

    function unlinkScope(scopeName: string) {
        if (props.scopes.includes(scopeName)) {
            scopeCodeSetChanges.value.push({
                scopeName: scopeName,
                changeType: 'Removed'
            });
        } else {
            const index = _.findIndex(scopeCodeSetChanges.value, {
                scopeName: scopeName,
                changeType: 'New'
            });
            scopeCodeSetChanges.value.splice(index, 1);
        }
    }

    function cancelUnlinkScope(scopeName: string) {
        const index = _.findIndex(scopeCodeSetChanges.value, {
            scopeName: scopeName,
            changeType: 'Removed'
        });
        scopeCodeSetChanges.value.splice(index, 1);
    }

    function linkScope(scopeName: string | null) {
        if (scopeName === null) return;
        if (scopeCodeSetChanges.value.map((s) => s.scopeName).includes(scopeName))
            return;
        if (props.scopes.includes(scopeName)) return;

        scopeCodeSetChanges.value.push({
            scopeName: scopeName,
            changeType: 'New'
        });
    }

    async function saveScopeCodeSetChanges() {
        const request: ScopeCodeSetRelease = {
            specificationType: 'ScopeCodeSetRelease',
            libraryName: null,
            codeSetName: props.codeset.name,
            codeSetDescription: props.codeset.description,
            applicationName: null,
            releaseId: 0,
            changes: scopeCodeSetChanges.value
        };
        const response = await createScopeCodeSetRelease(http, request);

        if (response) {
            await router.push(getReleaseLink(response.data.id));
        }

        return true;
    }

    async function doUpdateCodeSet() {
        try {
            saveLoading.value = true;
            await updateCodeSet(http, props.library.name, props.codeset.name, {
                description: newDescription.value ?? undefined,
                definition: newDefinition.value?.trim() ? newDefinition.value : '',
                codeNameRegex: newRegex.value,
                codeNameRegexErrorMessage: newRegexErrorMessage.value,
                lockedForDelete: newLockedForDelete.value,
                isReleaseReady: newIsReleaseReady.value ?? undefined,
                isValid: props.codeset.isValid
            });

            saveLoading.value = false;
            return true;
        } catch (err) {
            showError(
                _.get(err, 'response.data.Message', 'unable to update codeset')
            );
            // Sleep 4 sec to delay page reload's so that the user can read error message
            await new Promise((r) => setTimeout(r, 4000));
            saveLoading.value = false;
            return false;
        }
    }

    async function editDone() {
        const scopingIsEdited
            = !props.library.isGlobal && scopeCodeSetChanges.value.length > 0;

        const codeSetEdited
            = newDescription.value !== props.codeset.description
                || newDefinition.value !== (props.codeset.definition ?? '')
                || newLockedForDelete.value !== props.codeset.lockedForDelete
                || newIsReleaseReady.value !== props.codeset.isReleaseReady
                || newRegex.value !== props.codeset.codeNameRegex
                || newRegexErrorMessage.value !== props.codeset.codeNameRegexErrorMessage;

        if (scopingIsEdited) {
            const scopesLen = props.scopes.length;
            const changedScopesLen = scopeCodeSetChanges.value.length;
            const removedScopesLen = scopeCodeSetChanges.value.filter(
                (x) => x.changeType === 'Removed'
            ).length;

            if (scopesLen === removedScopesLen && removedScopesLen === changedScopesLen) {
                showError('Can\'t remove all scopes!');
                return;
            }
        }

        let codeSetUpdated = false;

        if (codeSetEdited) {
            codeSetUpdated = await doUpdateCodeSet();
        }

        if (scopingIsEdited) {
            await saveScopeCodeSetChanges();
            closeModal();
        }

        if (codeSetUpdated && !scopingIsEdited) {
            emit('refresh');
            closeModal();
        }
    }

    function closeModal() {
        emit('close');
    }
</script>

<template>
    <div
        class="modal-card">
        <header class="modal-card-head">
            <p class="modal-card-title">
                <b-icon
                    icon="cog"
                    size="is-small" /> Library Set settings
            </p>
        </header>
        <section class="modal-card-body">
            <div class="columns is-desktop">
                <div class="column is-narrow narrow-column">
                    <div>
                        <b-field label="Short description">
                            <b-input v-model="newDescription" />
                        </b-field>
                        <b-field
                            v-if="canEditRegex"
                            label="Regex for names in this library set"
                            :message="regexError"
                            :type="regexError ? 'is-danger' : ''">
                            <b-input v-model="newRegex" />
                        </b-field>
                        <b-field
                            v-if="canEditRegex"
                            label="Invalid Regex message">
                            <b-input v-model="newRegexErrorMessage" />
                        </b-field>
                        <div
                            v-show="!library.isGlobal"
                            class="field mb-4">
                            <label class="label">Scope linked to this library set</label>
                            <div class="field has-addons mb-4">
                                <b-select
                                    v-model="selectedScope"
                                    :required="true"
                                    expanded
                                    placeholder="Select your option">
                                    <option
                                        v-for="scope in scopesNotSelected"
                                        :key="scope"
                                        :value="scope">
                                        {{ scope }}
                                    </option>
                                </b-select>
                                <div class="control">
                                    <b-button
                                        v-require-can-edit-library="library.name"
                                        type="is-info"
                                        icon-left="plus"
                                        @click="linkScope(selectedScope)">
                                        Add
                                    </b-button>
                                </div>
                            </div>

                            <b-loading
                                v-model="loading"
                                :is-full-page="false"
                                :can-cancel="false" />
                            <div class="field is-grouped is-grouped-multiline">
                                <div
                                    v-for="(scope, idx) in scopeViewModel"
                                    :key="idx"
                                    class="control">
                                    <div
                                        v-if="scope.changeType == 'New'"
                                        class="tags has-addons">
                                        <span class="tag is-medium is-dark">
                                            <i class="mdi mdi-plus" />&nbsp;{{ scope.name }}
                                        </span>
                                        <a
                                            class="tag is-medium is-primary"
                                            @click="unlinkScope(scope.name)">
                                            <i class="mdi mdi-eraser" />
                                        </a>
                                    </div>

                                    <div
                                        v-else-if="scope.changeType == 'Removed'"
                                        class="tags has-addons">
                                        <span class="tag is-medium is-dark">
                                            <i class="mdi mdi-minus" />&nbsp;<span class="is-strikeout">{{
                                                scope.name
                                            }}</span>
                                        </span>
                                        <a
                                            class="tag is-medium is-info"
                                            @click="cancelUnlinkScope(scope.name)">
                                            <i class="mdi mdi-reload" />
                                        </a>
                                    </div>

                                    <div
                                        v-else
                                        class="tags has-addons">
                                        <span class="tag is-medium is-dark">{{ scope.name }}</span>
                                        <a
                                            v-require-can-edit-library="library.name"
                                            class="tag is-medium is-primary"
                                            @click="unlinkScope(scope.name)">
                                            <i class="mdi mdi-trash-can" />
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="field">
                            <label class="label">Options</label>
                            <b-checkbox
                                v-model="newLockedForDelete"
                                class="mb-2">
                                <b-icon
                                    :icon="newLockedForDelete ? 'lock' : 'lock-off'"
                                    size="is-small"
                                    :type="newLockedForDelete ? '' : 'is-primary'" /> Locked: Item delete not allowed
                            </b-checkbox>
                            <b-checkbox v-model="newIsReleaseReady">
                                <b-icon
                                    :icon="newIsReleaseReady ? 'check' : 'close'"
                                    size="is-small"
                                    :type="newIsReleaseReady ? '' : 'is-primary'" /> Ready for release
                            </b-checkbox>
                        </div>
                    </div>
                </div>
                <div class="field is-flex is-flex-direction-column definition-column">
                    <b-field class="label">
                        Definition
                    </b-field>
                    <div class="is-flex-grow-1">
                        <markdown-editor-component
                            v-if="newDefinition !== null"
                            v-model="newDefinition!" />
                    </div>
                </div>
            </div>
        </section>
        <footer class="modal-card-foot is-justify-content-flex-end">
            <div class="buttons">
                <b-button
                    icon-left="close-circle"
                    @click="closeModal">
                    Cancel
                </b-button>
                <b-button
                    type="is-primary"
                    icon-left="floppy"
                    :loading="saveLoading"
                    :disabled="saveDisabled"
                    @click="editDone">
                    Update Library Set
                </b-button>
            </div>
        </footer>
    </div>
</template>

<style scoped>
 a[disabled]{
     opacity: 0.6;
 }

 .modal-card {
     max-width: 100%;
     height: auto;
     width: 100%;
 }

 .narrow-column {
     width: 25rem;
 }

 .definition-column {
     min-width: 30rem;
 }

</style>
